import React, {Fragment, useCallback, useEffect, useMemo, useState} from "react";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FolderIcon from "@mui/icons-material/Folder";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import HeightIcon from "@mui/icons-material/Height";
import StorageIcon from "@mui/icons-material/Storage";
import VerticalAlignCenterIcon from "@mui/icons-material/VerticalAlignCenter";
import {Box, Button, Grid, IconButton, Tooltip} from "@mui/material";
import _ from "lodash";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import {Virtuoso} from "react-virtuoso";
import {getPackagesInternalUrl} from "../../constants/appRoutes";
import CatalogInfoButton from "../catalog-info-button";
import CustomLink from "../custom-link";
import {
  themeConfigShowPackageListInCategoriesTree,
  themeConfigShowStatViewerInfoButton
} from "../../state/theme-config/themeConfigSelectors";
import {getPackageExternalResources} from "../../utils/catalog";
import "./style.css";

const PATH_SEPARATOR = "+";

const TREE_NODE_TYPE_PACKAGE = "package";

const CategoriesTree = ({
  node,
  catalog,
  initialPath,
  showPackageList: showPackageListProp = false,
  showExpandControls = false,
  showCategoriesFirst = false,
  selectedCategories,
  selectedPackage,
  onPackageClick,
  onClose,
  height
}) => {
  const {t} = useTranslation();

  const showInfoButton = useSelector(themeConfigShowStatViewerInfoButton);
  const showPackageListThemeConfig = useSelector(themeConfigShowPackageListInCategoriesTree);

  const [allData, setAllData] = useState(null); // whole catalog presented as a flat list
  const [visibleData, setVisibleData] = useState(null); // current visible data (portion of allData)

  const [expandedNodes, setExpandedNodes] = useState({});
  const [cachedExpandedNodes, setCachedExpandedNodes] = useState({});

  // function that formats a category and its children, both categories and packages
  const getFormattedCategory = useCallback(
    category => {
      const formattedCategories = [];
      const formattedPackages = [];

      if ((category?.childrenCategories || []).length > 0) {
        category.childrenCategories.forEach(childCategory => {
          formattedCategories.push({
            ...getFormattedCategory(childCategory),
            parentId: category.id,
            parentPath: category.path
          });
        });
      }

      if ((category?.packageIdentifiers || []).length > 0) {
        if (showPackageListProp || showPackageListThemeConfig) {
          category.packageIdentifiers.forEach(id => {
            if (catalog.packageMap[id]) {
              formattedPackages.push({
                ...catalog.packageMap[id],
                type: TREE_NODE_TYPE_PACKAGE,
                id: id,
                parentId: category.id,
                parentPath: category.path,
                label: catalog.packageMap[id].title
              });
            }
          });
        } else {
          formattedPackages.push({
            type: TREE_NODE_TYPE_PACKAGE,
            id: "",
            label: t("components.categoriesTree.goToPackage", {
              packageCount: category.packageIdentifiers.length
            })
          });
        }
      }

      const children = showCategoriesFirst
        ? [...formattedCategories, ...formattedPackages]
        : [...formattedPackages, ...formattedCategories];

      return {
        ...category,
        children: children,
        childrenCategories: null,
        packageIdentifiers: null
      };
    },
    [t, catalog.packageMap, showCategoriesFirst, showPackageListProp, showPackageListThemeConfig]
  );

  // function that creates the category tree from the catalog, also adding the root level packages to the root of the tree
  const getFormattedCatalogTree = useCallback(
    catalog => {
      const rootCatalogCategories =
        catalog.categoryGroups.length > 0
          ? catalog.hasCategorySchemes
            ? catalog.categoryGroups.map(({id, label, categories}) => ({id, label, childrenCategories: categories}))
            : catalog.categoryGroups[0].categories
          : [];

      const formattedCatalog = rootCatalogCategories.map(rootCategory => getFormattedCategory(rootCategory));

      (catalog?.rootPackages || []).forEach(rootPackage =>
        formattedCatalog.push({
          ...rootPackage,
          type: TREE_NODE_TYPE_PACKAGE,
          id: rootPackage.identifier,
          parentId: rootPackage.categoryPath[1],
          parentPath: rootPackage.categoryPath[1],
          label: rootPackage.title
        })
      );

      return formattedCatalog;
    },
    [getFormattedCategory]
  );

  // function that formats uncategorized packages
  const getFormattedUncategorizedPackages = useCallback(
    (uncategorizedPackages, packageMap) => {
      return showPackageListProp || showPackageListThemeConfig
        ? (uncategorizedPackages || []).map(packageId => ({
            ...packageMap[packageId],
            type: TREE_NODE_TYPE_PACKAGE,
            id: packageId,
            parentId: "uncategorized",
            parentPath: "uncategorized",
            label: packageMap[packageId].title
          }))
        : [
            {
              type: TREE_NODE_TYPE_PACKAGE,
              id: "",
              label: t("components.categoriesTree.goToPackage", {
                packageCount: uncategorizedPackages.length
              })
            }
          ];
    },
    [t, showPackageListProp, showPackageListThemeConfig]
  );

  // formatted tree containing special "uncategorized" category for uncategorized packages
  const catalogTree = useMemo(() => {
    const catalogTree = getFormattedCatalogTree(catalog);
    if ((catalog?.uncategorizedPackages || []).length > 0) {
      catalogTree.push({
        id: "uncategorized",
        label: t("commons.catalog.uncategorized"),
        children: getFormattedUncategorizedPackages(catalog.uncategorizedPackages, catalog.packageMap)
      });
    }
    return catalogTree;
  }, [t, catalog, getFormattedCatalogTree, getFormattedUncategorizedPackages]);

  // function that creates a flat list with data from the whole catalog
  const getAllData = useCallback(
    tree => {
      const data = [];

      const traverseTree = (nodes, parent = null) => {
        nodes.forEach(item => {
          const path = parent
            ? `${parent.path}${PATH_SEPARATOR}${item.id}`
            : [...(initialPath || []), item.id].join(PATH_SEPARATOR);

          const node = {
            ...item,
            id: `${item.id}`,
            parentId: parent ? parent.id : undefined,
            parentPath: parent ? parent.path : undefined,
            children: null,
            hasChildren: item.children && item.children.length > 0,
            path: path,
            level: parent ? path.split(PATH_SEPARATOR).length - 1 - (initialPath || []).length : 0
          };

          data.push(node);

          if ((item?.children || []).length > 0) {
            traverseTree(item.children, node);
          }
        });
      };

      traverseTree(tree);

      return data;
    },
    [initialPath]
  );

  // funtion that return the list of expanded nodes
  const getVisibleData = useCallback((allData, idsToShow, expandedNodes) => {
    const newExpandedNodes = _.cloneDeep(expandedNodes);
    const newVisibleData = [];

    allData.forEach((data, _, arr) => {
      if (data.level === 0) {
        newVisibleData.push({...data});
        if (data.hasChildren && idsToShow.includes(data.id)) {
          newExpandedNodes[data.path] = true;
        } else {
          delete newExpandedNodes[data.path];
        }
      }
      if (data.hasChildren && idsToShow.includes(data.id)) {
        const children = arr.filter(a => a.parentPath === data.path);
        const index = newVisibleData.findIndex(element => element.path === data.path);
        if (index !== -1) {
          newExpandedNodes[newVisibleData[index].path] = true;
          newVisibleData.splice(+index + 1, 0, ...children);
        }
      }
    });

    setExpandedNodes(newExpandedNodes);
    return newVisibleData;
  }, []);

  const getInitialVisibleData = useCallback(allData => {
    const firstLevelData = allData.filter(data => data.level === 0);
    return firstLevelData;
  }, []);

  useEffect(() => {
    if (catalogTree) {
      const newAllData = getAllData(catalogTree);
      setAllData(newAllData);
    }
  }, [catalogTree, getAllData]);

  useEffect(() => {
    if (allData) {
      if (selectedCategories) {
        const idsToShow = [];
        const expandedNodes = {};
        selectedCategories.forEach(categoryId => {
          expandedNodes[categoryId] = true;
          const category = allData.find(data => data.id === categoryId);
          if (category) {
            const pathSplitted = category.path.split(PATH_SEPARATOR);
            pathSplitted.forEach(ps => {
              if (!idsToShow.includes(ps)) {
                idsToShow.push(ps);
              }
            });
          }
        });

        const newVisibleData = getVisibleData(allData, idsToShow, expandedNodes);
        setVisibleData(newVisibleData);
      } else {
        setExpandedNodes({});
        setCachedExpandedNodes({});
        const newVisibleData = getInitialVisibleData(allData);
        setVisibleData(newVisibleData);
      }
    }
  }, [allData, selectedCategories, getVisibleData, getInitialVisibleData]);

  const onExpandOrCollapse = useCallback(
    (index, node) => {
      const newExpandedNodes = _.cloneDeep(expandedNodes);

      if (!newExpandedNodes[visibleData[index].path]) {
        // expand case

        const children = cachedExpandedNodes[node.path]
          ? cachedExpandedNodes[node.path]
          : allData.filter(data => data.parentPath === node.path);

        delete cachedExpandedNodes[node.path];
        setCachedExpandedNodes(cachedExpandedNodes);

        const newVisibleData = _.cloneDeep(visibleData);
        newVisibleData.splice(+index + 1, 0, ...children);
        setVisibleData(newVisibleData);

        newExpandedNodes[newVisibleData[index].path] = true;
      } else {
        // collapse case

        delete newExpandedNodes[visibleData[index].path];
        const descendants = visibleData.filter(data => data.path.startsWith(`${node.path}${PATH_SEPARATOR}`));
        const newCachedExpanded = {[node.path]: descendants, ...cachedExpandedNodes};
        setCachedExpandedNodes(newCachedExpanded);

        const newVisibleData = visibleData.filter(data => !data.path.startsWith(`${node.path}${PATH_SEPARATOR}`));
        setVisibleData(newVisibleData);
      }

      setExpandedNodes(newExpandedNodes);
    },
    [allData, visibleData, expandedNodes, cachedExpandedNodes]
  );

  const onExpandAll = useCallback(() => {
    const newExpandedNodes = _.cloneDeep(expandedNodes);
    const newVisibleData = [];
    allData.forEach(data => {
      newVisibleData.push(_.cloneDeep(data));
      if (data.hasChildren) {
        newExpandedNodes[data.path] = true;
      } else {
        delete newExpandedNodes[data.path];
      }
    });
    setExpandedNodes(newExpandedNodes);
    setVisibleData(newVisibleData);
  }, [allData, expandedNodes]);

  const onCollapseAll = useCallback(() => {
    setExpandedNodes({});
    setCachedExpandedNodes({});
    const newVisibleData = getInitialVisibleData(allData);
    setVisibleData(newVisibleData);
  }, [allData, getInitialVisibleData]);

  const handleKeyDown = useCallback(
    (event, index, d) => {
      if (event.key === "Enter" || event.key === " ") {
        onExpandOrCollapse(index, d);
      }
    },
    [onExpandOrCollapse]
  );

  return (
    <Box className="categories-tree" sx={{color: theme => theme.palette.text.primary}}>
      {showExpandControls && catalog.categoryGroups.length > 0 && (
        <Grid container spacing={1} sx={{marginBottom: "2px"}} justifyContent="flex-end">
          <Grid>
            <Tooltip title={t("components.categoriesTree.expandAll.tooltip")}>
              <IconButton
                aria-label={t("components.categoriesTree.expandAll.ariaLabel")}
                sx={{padding: "8px"}}
                onClick={() => onExpandAll()}
              >
                <HeightIcon />
              </IconButton>
            </Tooltip>
          </Grid>
          <Grid>
            <Tooltip title={t("components.categoriesTree.collapseAll.tooltip")}>
              <IconButton
                aria-label={t("components.categoriesTree.collapseAll.ariaLabel")}
                sx={{padding: "8px"}}
                onClick={() => onCollapseAll()}
              >
                <VerticalAlignCenterIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      )}
      <Box className="v-tree-tree-container">
        <Virtuoso
          data={visibleData}
          style={{height: height ?? "600px"}}
          itemContent={(index, pkg) => {
            const externalResources = getPackageExternalResources(pkg.id, catalog);
            return (
              <Box
                className={`v-tree-item-container ${[selectedPackage, ...(selectedCategories || [])].includes(pkg.id) ? "v-tree-item-container--selected" : ""}`}
                sx={{
                  marginLeft: `${pkg.level * 15}px`,
                  cursor: `pointer`
                }}
                onClick={() => onExpandOrCollapse(index, pkg)}
                tabIndex={!pkg.hasChildren ? -1 : 0}
                onKeyDown={event => handleKeyDown(event, index, pkg)}
              >
                {pkg.type !== TREE_NODE_TYPE_PACKAGE && pkg.hasChildren ? (
                  <Box className="v-tree-item-container-icon">
                    {expandedNodes[pkg.path] || false ? (
                      <ExpandMoreIcon fontSize="small" />
                    ) : (
                      <ChevronRightIcon fontSize="small" />
                    )}
                  </Box>
                ) : (
                  <Box className="v-tree-item-container-icon-placeholder" />
                )}
                {pkg.type === TREE_NODE_TYPE_PACKAGE ? (
                  <Tooltip title={pkg.label}>
                    {onPackageClick ? (
                      <Button
                        onClick={() => onPackageClick(pkg.id)}
                        startIcon={
                          <StorageIcon fontSize="small" className="categories-tree__tree-item__dataset__icon" />
                        }
                        className="categories-tree__tree-item__dataset__button"
                      >
                        <span className="categories-tree__tree-item__dataset__label">{pkg.label}</span>
                      </Button>
                    ) : (
                      <CustomLink
                        to={getPackagesInternalUrl(node.code.toLowerCase(), pkg.path.split(PATH_SEPARATOR))}
                        text={<span className="categories-tree__tree-item__dataset__label">{pkg.label}</span>}
                        icon={<StorageIcon fontSize="small" className="categories-tree__tree-item__dataset__icon" />}
                        textStyle={{paddingLeft: 0}}
                        onClick={onClose}
                      />
                    )}
                    {showInfoButton &&
                      (pkg.source || pkg.description || (pkg.resourceIdentifiers || []).length > 0) && (
                        <Box
                          sx={{marginLeft: "4px", "& button": {padding: "4px"}}}
                          className={"categories-tree__tree-item__dataset__action"}
                        >
                          <CatalogInfoButton
                            title={pkg.label}
                            source={pkg.source}
                            description={pkg.description}
                            attachments={externalResources}
                          />
                        </Box>
                      )}
                  </Tooltip>
                ) : (
                  <Fragment>
                    {expandedNodes[pkg.path] || false ? (
                      <FolderOpenIcon sx={{marginRight: "8px"}} className="categories-tree__tree-item__node__icon" />
                    ) : (
                      <FolderIcon sx={{marginRight: "8px"}} className="categories-tree__tree-item__node__icon" />
                    )}
                    <span className="v-tree-item-container-label" style={{cursor: `pointer`}}>
                      {pkg.label}
                    </span>
                  </Fragment>
                )}
              </Box>
            );
          }}
        />
      </Box>
    </Box>
  );
};

export default CategoriesTree;
